import * as React from 'react';
import * as moment from 'moment';

interface Props {
  booking: any;
  vehicleType: any;
  hideTotals?: boolean;
}

/**
 * Displays pricing information for a booking
 */
const BookingPrices = ({ booking, hideTotals = false }: Props) => {
  const totalOutstanding = Number(
    (Number(booking.total_cost) - Number(booking.total_paid)).toFixed(2),
  );
  const duration = 1 + moment(booking.end).diff(moment(booking.start), 'd');
  const driverCount = booking.drivers
    ? Math.max(0, booking.drivers.length - 1)
    : 0;
  return (
    <>
      <table className="field">
        <tbody>
          <tr>
            <td>Hire Cost</td>
            <td className="has-text-right">
              <strong>£{Number(booking.hire_cost).toFixed(2)}</strong>
              <br />
              <small className="has-text-grey">
                £{Number(booking.hire_cost_per_day).toFixed(2)} &times;{' '}
                {duration} day{duration === 1 ? ' ' : 's '}
              </small>
            </td>
          </tr>
          {Number(booking.driver_cost) > 0 && (
            <tr>
              <td>Driver Cost</td>
              <td className="has-text-right">
                <strong>£{Number(booking.driver_cost).toFixed(2)}</strong>
                <br />
                <small className="has-text-grey">
                  {driverCount} additional driver{driverCount === 1 ? '' : 's'}
                </small>
              </td>
            </tr>
          )}
          {Number(booking.accessory_cost) > 0 && (
            <tr>
              <td>Accessory Cost</td>
              <td className="has-text-right">
                <strong>£{Number(booking.accessory_cost).toFixed(2)}</strong>
                {booking.accessories &&
                  booking.accessories.map((accessory) => (
                    <div key={accessory.id}>
                      <small className="has-text-grey">
                        {accessory.name} &times; {accessory.quantity}
                      </small>
                    </div>
                  ))}
              </td>
            </tr>
          )}
          {Number(booking.extra_mileage_cost) > 0 && (
            <tr>
              <td>
                Extra Mileage Cost
                <br />
              </td>
              <td className="has-text-right">
                <strong>
                  £{Number(booking.extra_mileage_cost).toFixed(2)}
                </strong>
                <br />
                <small className="has-text-grey">
                  £{Number(booking.extra_mileage_rate).toFixed(2)} &times;{' '}
                  {Math.round(
                    Number(booking.extra_mileage_cost) /
                      Number(booking.extra_mileage_rate),
                  )}{' '}
                  extra miles
                </small>
              </td>
            </tr>
          )}
          {Number(booking.additional_charge_cost) > 0 && (
            <tr>
              <td>Additional Charges</td>
              <td className="has-text-right">
                <strong>
                  £{Number(booking.additional_charge_cost).toFixed(2)}
                </strong>
                {booking.additional_charges &&
                  booking.additional_charges.map((charge) => (
                    <div key={charge.id}>
                      <small className="has-text-grey">
                        {charge.description}: £
                        {Number(charge.amount).toFixed(2)}
                      </small>
                    </div>
                  ))}
              </td>
            </tr>
          )}
          {Number(booking.zero_rated_charge_cost) > 0 && (
            <tr>
              <td>Zero Rated Charges</td>
              <td className="has-text-right">
                <strong>
                  £{Number(booking.zero_rated_charge_cost).toFixed(2)}
                </strong>
                {booking.zero_rated_charges &&
                  booking.zero_rated_charges.map((charge) => (
                    <div key={charge.id}>
                      <small className="has-text-grey">
                        {charge.description}: £
                        {Number(charge.amount).toFixed(2)}
                      </small>
                    </div>
                  ))}
              </td>
            </tr>
          )}
          {Number(booking.discount) > 0 && (
            <tr>
              <td>Discount</td>
              <td className="has-text-right">
                <strong>£{Number(booking.discount).toFixed(2)}</strong>
              </td>
            </tr>
          )}
          {booking.discount_note && (
            <tr>
              <td>Discount Note</td>
              <td className="has-text-right">
                <strong>{booking.discount_note}</strong>
              </td>
            </tr>
          )}
          {Number(booking.company_insurance_discount) > 0 && (
            <tr>
              <td>Company Insurance Discount</td>
              <td className="has-text-right">
                <strong>
                  £{Number(booking.company_insurance_discount).toFixed(2)}
                </strong>
              </td>
            </tr>
          )}
          <tr>
            <td>Security Cost</td>
            <td className="has-text-right">
              <strong>£{Number(booking.security_cost).toFixed(2)}</strong>
            </td>
          </tr>
          <tr>
            <td>Responsibility Amount</td>
            <td className="has-text-right">
              <strong>
                £{Number(booking.responsibility_amount).toFixed(2)}
              </strong>
            </td>
          </tr>
        </tbody>
      </table>
      {!hideTotals && (
        <>
          <hr />
          <table className="has-text-weight-bold">
            <tbody>
              <tr>
                <td>Total Cost</td>
                <td className="has-text-right">
                  <strong>£{Number(booking.total_cost).toFixed(2)}</strong>
                </td>
              </tr>
              <tr>
                <td>Total Paid</td>
                <td className="has-text-right">
                  <strong>£{Number(booking.total_paid).toFixed(2)}</strong>
                </td>
              </tr>
              <tr>
                <td>
                  Total {totalOutstanding < 0 ? 'Due Back' : 'Outstanding'}
                </td>
                <td className="has-text-right">
                  <strong className="is-size-4">
                    £{Math.abs(Number(totalOutstanding)).toFixed(2)}
                  </strong>
                </td>
              </tr>
            </tbody>
          </table>
        </>
      )}
    </>
  );
};

export default BookingPrices;
